<template>
  <section class="h-full mx-4" id="traderSitesManagement">
    <BaseTable
      v-if="traderSites"
      :headers="traderSitesHeaders"
      :list="traderSites"
      :total-records="traderSitesTotalCount"
      table-id="trader-sites-management"
      :items-per-page="traderSitesItemsPerPage"
      :selected-page="tradersSitesPageNumber"
      :group-by-headers="[
        {
          value: 'trader.name',
          text: `${$t('traderSites.traderName')}`,
          type: 'text',
          active: true,
        },
      ]"
      :is-add-new-hidden="true"
      @refresh-data="loadData"
      @deleteMultiselect="onDeleteMultiselect"
      clickable
      @table-item-clicked="onItemClick"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="deleteSingleConfirm(item as TraderSite)" />
        </div>
      </template>
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>
    <BaseModalConfirm
      v-if="confirmDeleteSingleModalActive"
      @cancel="confirmDeleteSingleModalActive = false"
      @confirm="onDeleteSingle">
      <template v-if="traderSiteToDeleteSingle" #text>
        {{ $t('messages.info.deleteSingle', { 'item': traderSiteToDeleteSingle.code }) }}</template
      >
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';

import { getTraderSitesWithConditions, removeTraderSitesByIds } from '@/services/api/traders';
import { useToastStore } from '@/stores/toast';

import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import type { TraderSite } from '@/types/Traders';
import type { TableFilterData } from '@/types/Base';

import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStateStore } from '@/stores/state';
const { t: $t } = useI18n({ useScope: 'global' });

const router = useRouter();
const traderSites = ref<TraderSite[]>();
const traderSitesItemsPerPage = ref(50);
const tradersSitesPageNumber = ref(1);
const traderSitesTotalCount = ref<number>();
const filterIsActive = ref<boolean | undefined>();

const traderSitesHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'trader.name', text: `${$t('traderSites.traderName')}`, type: 'text', active: true },
  { value: 'address', text: $t('address'), type: 'text', active: true },
  { value: 'zipCode', text: $t('zipCode'), type: 'text', active: true },

  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const { getTableSelectedPage } = useStateStore();

onMounted(async () => {
  const storedTableData = getTableSelectedPage('trader-sites-management');
  if (storedTableData) {
    traderSitesItemsPerPage.value = storedTableData.itemsPerPage;
    tradersSitesPageNumber.value = storedTableData.page;
  }
  await loadData({
    itemsPerPage: traderSitesItemsPerPage.value,
    sortColumn: 'name',
    sortAscending: true,
    page: tradersSitesPageNumber.value,
  });
});

async function loadData(data: TableFilterData) {
  const sortColumn = data.column !== undefined ? traderSitesHeaders[data.column]?.value : 'name';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    isActive: data.isActive,
  };
  const res = await getTraderSitesWithConditions(params);
  if (res) {
    traderSites.value = res.data;
    traderSitesTotalCount.value = res.count;
  }
}

const toastStore = useToastStore();

async function onDeleteSingle() {
  if (!traderSiteToDeleteSingle.value) return;
  const idToDelete = traderSiteToDeleteSingle.value.id;

  const res = await removeTraderSitesByIds([idToDelete]);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
  confirmDeleteSingleModalActive.value = false;
}

async function onDeleteMultiselect(items: TraderSite[]) {
  const idsToDelete = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  const res = await removeTraderSitesByIds(idsToDelete);

  if (!res && traderSites.value)
    traderSites.value = traderSites.value.filter((type) => !idsToDelete.includes(type.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

const confirmDeleteSingleModalActive = ref(false);
const traderSiteToDeleteSingle = ref<TraderSite>();

function deleteSingleConfirm(item: TraderSite) {
  traderSiteToDeleteSingle.value = item;
  confirmDeleteSingleModalActive.value = true;
}

function onItemClick(item: TraderSite) {
  router.push({ name: 'trader-sites-preview', params: { id: item.id } });
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  loadData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  loadData({});
}
</script>
